import { formatCurrency } from "@/lib/utils";
import { m } from "@/paraglide/messages";
import type { BidBattle } from "@bidbattles/bloks/lib/rapace";
import { cn } from "@bidbattles/bloks/lib/ui";
import { forwardRef } from "react";

const BidBattleImageWrapper = forwardRef<
	HTMLDivElement,
	React.ComponentProps<"div">
>(({ className, children, ...props }, ref) => {
	return (
		<div ref={ref} className={cn("relative", className)} {...props}>
			<div
				className="absolute top-0 left-0 right-0 h-2/5"
				style={{
					background:
						"linear-gradient(180deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.00) 100%)",
				}}
			/>
			<div
				className="absolute bottom-0 left-0 right-0 h-2/5"
				style={{
					background:
						"linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.50) 100%)",
				}}
			/>
			{children}
		</div>
	);
});

function BidBattleStatus({
	className,
	status,
}: { className?: string; status: BidBattle["status"] }) {
	return (
		<span
			className={cn(
				"absolute top-2 left-2 px-3 py-1 rounded-2xl bg-white text-gray-900 text-sm leading-5 font-semibold",
				status === "LIVE" && "text-red-700",
				className,
			)}
		>
			{m[`bidbattle__status__${status}`]()}
		</span>
	);
}

type BidBattleInfo = Pick<
	BidBattle,
	| "status"
	| "item_name"
	| "item_image_url"
	| "item_description"
	| "open_seats"
	| "total_seats"
	| "entry_price"
	| "winner_details"
>;

function BidBattleCard({
	battle,
	className,
	render,
}: {
	battle: BidBattleInfo;
	className?: string;
	render: (
		props: React.PropsWithChildren<{ className: string }>,
	) => React.ReactElement;
}) {
	const totalSeats = String(battle.total_seats);
	return render({
		className: cn(
			"bg-white flex flex-col select-none rounded-2xl shadow-none max-w-full",
			"focus-visible:shadow-xl focus-visible:shadow-gray-400",
			"focus-visible:outline-1 focus-visible:outline-offset-0 outline-gray-300",
			"ease-in-out duration-100",
			className,
		),
		children: (
			<>
				<BidBattleImageWrapper className="rounded-t-2xl overflow-hidden">
					<BidBattleStatus status={battle.status} />
					<img
						alt={battle.item_name}
						src={battle.item_image_url}
						className="w-full object-cover aspect-28/31"
						loading="lazy"
					/>
				</BidBattleImageWrapper>
				<div className="py-4 px-6 border-gray-300 rounded-b-2xl border-b border-l border-r grow flex flex-col gap-4 justify-between">
					<div>
						<p className="font-bold text-lg leading-7 text-gray-900">
							{battle.item_name}
						</p>
						<p className="leading-6 text-gray-900 text-ellipsis whitespace-nowrap overflow-hidden">
							{battle.item_description}
						</p>
					</div>
					<div className="flex justify-between">
						{battle.status === "FINISHED" && !!battle.winner_details?.name ? (
							<div>
								<p className="text-xs text-gray-500 leading-[1.125rem]">
									Winning Bid
								</p>
								<p className="font-bold">{battle.winner_details?.name}</p>
							</div>
						) : (
							<>
								<div>
									<p className="text-xs text-gray-500 leading-[1.125rem]">
										{m.bidbattle__open_seats()}
									</p>
									<p>
										<span className="font-bold">
											{String(battle.total_seats - battle.open_seats).padStart(
												totalSeats.length,
												"0",
											)}
										</span>
										/{totalSeats}
									</p>
								</div>
								<div>
									<p className="text-xs text-gray-500 leading-[1.125rem]">
										{m.bidbattle__entry_price()}
									</p>
									<p className="font-bold">
										{formatCurrency("EUR", battle.entry_price)}
									</p>
								</div>
							</>
						)}
					</div>
				</div>
			</>
		),
	});
}

export type { BidBattleInfo };
export { BidBattleImageWrapper, BidBattleStatus, BidBattleCard };
